import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ajuda as api,
  type PedidoDeRota,
  type RotaDoPortal,
  type Verbete as VerbeteCompleto,
  type VerbeteNaLista,
} from '../api/ajuda'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { Verbete } from '../componentes/Verbete'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'
import { mensagemDeErro } from './mensagemDeErro'
import { marcaDaRota } from './marcaDaRota'
import { precisaEscolherMarca, resolverRota } from './resolverRota'
import './usuarios.css'
import './ajuda.css'

/** As marcas que o editor oferece. Fechada de propósito — ver `verbete.css`. */
const MARCAS = ['negrito', 'codigo', 'destaque', 'atencao', 'perigo', 'sucesso'] as const

const MOLDE: Record<(typeof MARCAS)[number], (t: string) => string> = {
  negrito: (t) => `**${t}**`,
  codigo: (t) => `\`${t}\``,
  destaque: (t) => `[[destaque: ${t}]]`,
  atencao: (t) => `[[atencao: ${t}]]`,
  perigo: (t) => `[[perigo: ${t}]]`,
  sucesso: (t) => `[[sucesso: ${t}]]`,
}

export function Ajuda() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const podeEditar = pode('ParametroGerenciar')
  const marca = marcaDaRota() ?? undefined

  const [lista, setLista] = useState<VerbeteNaLista[] | null>(null)
  const [rotas, setRotas] = useState<RotaDoPortal[]>([])
  const [categoria, setCategoria] = useState('')
  const [busca, setBusca] = useState('')
  const [buscou, setBuscou] = useState(false)
  const [falhou, setFalhou] = useState(false)
  const [aberto, setAberto] = useState<number | null>(null)
  const [editando, setEditando] = useState<number | 'novo' | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const [verbetes, catalogo] = await Promise.all([
        api.listar({ categoria: categoria || undefined, busca: busca || undefined }),
        api.rotas(),
      ])

      setLista(verbetes)
      setRotas(catalogo)
    } catch {
      setFalhou(true)
    }
  }, [categoria, busca])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function exportar() {
    try {
      const arquivo = await api.baixarExportacao()
      const url = URL.createObjectURL(arquivo)
      const link = document.createElement('a')
      link.href = url
      link.download = 'ajuda-do-portal.md'
      link.click()
      URL.revokeObjectURL(url)
    } catch (erro) {
      setRecado(mensagemDeErro(erro, t))
    }
  }

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  const categorias = [...new Set(lista.map((v) => v.categoria))].sort()

  return (
    <section className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('ajuda.titulo')}</h1>
          <p className="usuarios__descricao">{t('ajuda.descricao')}</p>
        </div>

        <div className="ajuda__acoes">
          <Botao variante="secundario" onClick={exportar}>
            {t('ajuda.exportar')}
          </Botao>

          {podeEditar && (
            <Botao onClick={() => setEditando('novo')}>{t('ajuda.novo')}</Botao>
          )}
        </div>
      </header>

      {recado && <Aviso tom="perigo">{recado}</Aviso>}

      <Cartao titulo={t('ajuda.filtros')}>
        <div className="ajuda__filtros">
          <label className="ajuda__filtro">
            <span>{t('ajuda.buscar')}</span>
            <input
              className="campo__entrada"
              type="search"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value)
                setBuscou(e.target.value.trim().length > 0)
              }}
              placeholder={t('ajuda.buscarDica')}
            />
          </label>

          <label className="ajuda__filtro">
            <span>{t('ajuda.categoria')}</span>
            <select
              className="campo__entrada"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
            >
              <option value="">{t('ajuda.todasCategorias')}</option>
              {categorias.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Cartao>

      {editando !== null && (
        <Editor
          id={editando}
          rotas={rotas}
          aoFechar={() => setEditando(null)}
          aoSalvar={async (mensagem) => {
            setEditando(null)
            setRecado(mensagem)
            await carregar()
          }}
        />
      )}

      {lista.length === 0 ? (
        // Busca sem resultado NÃO é o mesmo que vazio: uma diz "não existe",
        // a outra diz "seu filtro escondeu". Tratar as duas igual ensina o
        // leitor a achar que a ajuda está vazia.
        buscou || categoria ? (
          <Estado
            tipo="vazio"
            titulo={t('ajuda.semResultado')}
            descricao={t('ajuda.semResultadoDescricao')}
            acao={
              <Botao
                variante="secundario"
                onClick={() => {
                  setBusca('')
                  setCategoria('')
                  setBuscou(false)
                }}
              >
                {t('ajuda.limparFiltro')}
              </Botao>
            }
          />
        ) : (
          <Estado
            tipo="vazio"
            titulo={t('ajuda.vazio')}
            descricao={t('ajuda.vazioDescricao')}
            acao={podeEditar ? <Botao onClick={() => setEditando('novo')}>{t('ajuda.novo')}</Botao> : undefined}
          />
        )
      ) : (
        categorias.map((c) => (
          <Cartao key={c} titulo={c}>
            <ul className="ajuda__lista">
              {lista
                .filter((v) => v.categoria === c)
                .map((v) => (
                  <li key={v.id} className="ajuda__item">
                    <div className="ajuda__linha">
                      <button
                        type="button"
                        className="ajuda__abrir"
                        aria-expanded={aberto === v.id}
                        onClick={() => setAberto(aberto === v.id ? null : v.id)}
                      >
                        {v.titulo}
                      </button>

                      <span className={`ajuda__situacao ajuda__situacao--${v.situacao.toLowerCase()}`}>
                        {t(`ajuda.situacao.${v.situacao.toLowerCase()}`)}
                      </span>

                      {podeEditar && (
                        <Botao variante="secundario" onClick={() => setEditando(v.id)}>
                          {t('ajuda.editar')}
                        </Botao>
                      )}
                    </div>

                    {v.rotas.length > 0 && (
                      <ul className="ajuda__rotas">
                        {v.rotas.map((r) => (
                          <li key={`${r.tipo}-${r.caminho}`}>
                            <Link rota={r} marca={marca} />
                          </li>
                        ))}
                      </ul>
                    )}

                    {aberto === v.id && <Corpo id={v.id} marca={marca} />}

                    {v.atualizadoEm && (
                      <p className="ajuda__rodape">
                        {t('ajuda.atualizadoEm', {
                          data: formatar.dataHora(v.atualizadoEm),
                          quem: v.atualizadoPor ?? '—',
                        })}
                      </p>
                    )}
                  </li>
                ))}
            </ul>
          </Cartao>
        ))
      )}
    </section>
  )
}

/**
 * O link de uma rota.
 *
 * 🔴 Quando o leitor não alcança a tela, o link fica **inerte e diz qual
 * permissão falta**. Esconder ensinaria que o recurso não existe; prometer e
 * devolver 403 é pior — ele já clicou.
 */
function Link({ rota, marca }: { rota: VerbeteNaLista['rotas'][number]; marca?: string }) {
  const { t } = useTranslation()

  if (!rota.alcancavel)
    return (
      <span className="ajuda__rota ajuda__rota--inerte">
        {rota.rotulo}
        <span className="ajuda__exige">{t('ajuda.exige', { permissao: rota.exige })}</span>
      </span>
    )

  if (rota.tipo === 'Externa')
    return (
      <a className="ajuda__rota" href={rota.caminho} target="_blank" rel="noopener noreferrer">
        {rota.rotulo}
        <span className="ajuda__externo" aria-label={t('ajuda.novaAba')}>
          ↗
        </span>
      </a>
    )

  return (
    <a className="ajuda__rota" href={resolverRota(rota.caminho, marca)}>
      {rota.rotulo}
      {precisaEscolherMarca(rota.caminho, marca) && (
        <span className="ajuda__exige">{t('ajuda.escolhaMarca')}</span>
      )}
    </a>
  )
}

function Corpo({ id, marca }: { id: number; marca?: string }) {
  const { t } = useTranslation()
  const [verbete, setVerbete] = useState<VerbeteCompleto | null>(null)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    let vivo = true

    api
      .ler(id)
      .then((v) => vivo && setVerbete(v))
      .catch(() => vivo && setFalhou(true))

    return () => {
      vivo = false
    }
  }, [id])

  if (falhou) return <Estado tipo="erro" />
  if (!verbete) return <Estado tipo="carregando" />

  return (
    <div className="ajuda__corpo">
      <Verbete blocos={verbete.blocos} marca={marca} />
      <p className="ajuda__nota">{t('ajuda.conteudoDeProduto')}</p>
    </div>
  )
}

function Editor({
  id,
  rotas,
  aoFechar,
  aoSalvar,
}: {
  id: number | 'novo'
  rotas: RotaDoPortal[]
  aoFechar: () => void
  aoSalvar: (mensagem: string) => void | Promise<void>
}) {
  const { t } = useTranslation()

  const area = useRef<HTMLTextAreaElement>(null)

  const [titulo, setTitulo] = useState('')
  const [categoria, setCategoria] = useState('')
  const [ordem, setOrdem] = useState(0)
  const [conteudo, setConteudo] = useState('')
  const [escolhidas, setEscolhidas] = useState<PedidoDeRota[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (id === 'novo') return

    void api.ler(id).then((v) => {
      setTitulo(v.titulo)
      setCategoria(v.categoria)
      setOrdem(v.ordem)
      setConteudo(v.conteudo)
      setEscolhidas(v.rotas.map((r) => ({ tipo: r.tipo, caminho: r.caminho, rotulo: null })))
    })
  }, [id])

  /** Envolve a seleção com a marca. É a única forma de formatar — sem cor. */
  function marcar(marca: (typeof MARCAS)[number]) {
    const campo = area.current
    if (!campo) return

    const { selectionStart: a, selectionEnd: b } = campo
    const selecionado = conteudo.slice(a, b) || t('ajuda.textoExemplo')

    setConteudo(conteudo.slice(0, a) + MOLDE[marca](selecionado) + conteudo.slice(b))
    campo.focus()
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    setErro(null)
    setSalvando(true)

    const pedido = { titulo, conteudo, categoria, ordem, rotas: escolhidas }

    try {
      if (id === 'novo') await api.criar(pedido)
      else await api.salvar(id, pedido)

      await aoSalvar(t('ajuda.salvo'))
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Cartao titulo={id === 'novo' ? t('ajuda.novo') : t('ajuda.editar')}>
      <form className="ajuda__formulario" onSubmit={enviar}>
        {erro && <Aviso tom="perigo">{erro}</Aviso>}

        <Campo
          rotulo={t('ajuda.tituloDoVerbete')}
          obrigatorio
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
        />

        <Campo
          rotulo={t('ajuda.categoria')}
          obrigatorio
          ajuda={t('ajuda.categoriaDica')}
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
        />

        <Campo
          rotulo={t('ajuda.ordem')}
          type="number"
          ajuda={t('ajuda.ordemDica')}
          value={ordem}
          onChange={(e) => setOrdem(Number(e.target.value))}
        />

        <div className="ajuda__marcas" role="group" aria-label={t('ajuda.marcas')}>
          {MARCAS.map((m) => (
            <button key={m} type="button" className="ajuda__marca" onClick={() => marcar(m)}>
              {t(`ajuda.marca.${m}`)}
            </button>
          ))}
        </div>

        <p className="ajuda__nota">{t('ajuda.semCor')}</p>

        <label className="campo">
          <span className="campo__rotulo">{t('ajuda.conteudo')}</span>
          <textarea
            ref={area}
            className="campo__entrada ajuda__area"
            rows={12}
            required
            value={conteudo}
            onChange={(e) => setConteudo(e.target.value)}
          />
        </label>

        <fieldset className="ajuda__rotasEditor">
          <legend>{t('ajuda.telas')}</legend>
          <p className="ajuda__nota">{t('ajuda.telasDica')}</p>

          {escolhidas.map((r, i) => (
            <div key={i} className="ajuda__rotaLinha">
              <span className="ajuda__rotaCaminho">{r.caminho}</span>
              <Botao
                variante="secundario"
                onClick={() => setEscolhidas(escolhidas.filter((_, j) => j !== i))}
              >
                {t('ajuda.remover')}
              </Botao>
            </div>
          ))}

          <label className="ajuda__filtro">
            <span>{t('ajuda.acrescentarTela')}</span>
            <select
              className="campo__entrada"
              value=""
              onChange={(e) => {
                if (!e.target.value) return
                if (escolhidas.some((r) => r.caminho === e.target.value)) return

                setEscolhidas([
                  ...escolhidas,
                  { tipo: 'Interna', caminho: e.target.value, rotulo: null },
                ])
              }}
            >
              <option value="">{t('ajuda.escolha')}</option>
              {rotas.map((r) => (
                <option key={r.caminho} value={r.caminho}>
                  {r.nome} — {r.caminho}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <div className="ajuda__acoes">
          <Botao type="submit" carregando={salvando}>
            {t('comum.salvar')}
          </Botao>
          <Botao variante="secundario" onClick={aoFechar}>
            {t('comum.cancelar')}
          </Botao>
        </div>
      </form>
    </Cartao>
  )
}
